import hashUtils from 'app-lib/hashUtils'
import { ReactComponent as ArrowPagination } from 'bzx-common/src/assets/images/fulcrum/icon_pagination.svg'
import { supportedChains } from 'bzx-common/src/config/appConfig'
import chainConfigs from 'bzx-common/src/config/chains'
import { Button, ButtonClipboard, Icon, InfoTip, Input, Loader, Modal } from 'ui-framework'
import { observer } from 'mobx-react'
import React from 'react'
import EventInfo from 'shared-components/EventInfo'
import { ExternalLink } from 'shared-components/ExternalLink/ExternalLink'
import Picture from 'shared-components/Picture'
import { AvatarSelector } from './AvatarSelector'
import UserProfileVM from './UserProfileVM'

const loader = (
  <div className="flex-center">
    <Loader quantityDots={3} sizeDots="small" />
  </div>
)

export function UserProfile({ vm }: { vm: UserProfileVM }) {
  const { web3Connection } = vm.rootStore
  const { chainId, walletAddress, isConnected, explorerWalletLink } = web3Connection
  const { latestUserTransactions } = vm.statsStore

  if (!chainId || !isConnected) {
    return (
      <div className="txt-center margin-top-2">
        <img src="/images/no-trading-or-lending.svg" height="128px" />
        <div className="h3--muted txt-size-12 font-normal margin-top-05">
          Connect to see your profile
        </div>
      </div>
    )
  }

  return (
    <div className="user-profile">
      <main>
        <div className="user-profile__grid center-block txt-center">
          <div>
            <div className="panel--simple md-padded-2">
              <div className="user-profile__avatar margin-bottom-2">
                <div className="txt-title txt-muted margin-bottom-05">Avatar</div>
                <Button
                  variant="invisible"
                  className="user-profile__avatar-image user-profile__image-border center-block margin-bottom-2"
                  value={vm.avatarPagination}
                  title="Update avatar"
                  onClick={vm.avatarDialog.show}>
                  {vm.avatar ? (
                    <Picture
                      src={`/images/avatars/${vm.avatar}.png`}
                      webp={`/images/avatars/${vm.avatar}.webp`}
                      width={105}
                      height={105}
                    />
                  ) : (
                    <Icon name="User" size={105} />
                  )}
                  <Icon name="save" size={20} className="user-profile__edit" />
                </Button>

                <div className="txt-title txt-muted margin-bottom-05">Wallet Address</div>
                <ExternalLink className="link--discreet margin-right-05" href={explorerWalletLink}>
                  <span className="margin-left-05" title={walletAddress}>
                    {hashUtils.shortHash(walletAddress)}
                  </span>
                </ExternalLink>
                <ButtonClipboard text={walletAddress} top={-2} />
              </div>

              <span className="txt-title txt-muted">Address Alias </span>
              <InfoTip id="tip-user" top={-1}>
                <p>The alias and avatar will be displayed in the app.</p>
                <p>They are saved on your device only.</p>
              </InfoTip>
              <div className="input-with-sticked-btn margin-top-05 center-block" title={vm.warning}>
                <Input
                  id="input-alias"
                  className="input-rectangle txt-muted"
                  name="alias"
                  value={vm.inputAlias}
                  placeholder={vm.alias}
                  onChange={vm.ediAlias}
                  onChangeEmit="value"
                />

                <Button variant="blue" disabled={!vm.canSave} onClick={vm.saveAlias}>
                  Save
                </Button>
              </div>
              <div className="txt-warning margin-top-1" title={walletAddress}>
                {vm.warning}
              </div>

              <Button variant="invisible" className="link--discreet" onClick={vm.restoreDefaults}>
                Restore defaults
              </Button>
            </div>
          </div>

          <div className="flex-col-center panel--simple md-padded-2">
            <div className="flex-col-center">
              <h3 className="txt-title">Latest transactions across blockchains</h3>
              <div className="btn--sticked-horizontal margin-bottom-2">
                <Button
                  variant="tab-blue"
                  onClick={vm.set}
                  onClickEmit="name-value"
                  name="selectedChain"
                  size="narrow-fixed"
                  value={null}
                  active={!vm.selectedChain}>
                  All
                </Button>
                {supportedChains.map((chainId) => {
                  const { icon, chainDetails } = chainConfigs[chainId]
                  return (
                    <Button
                      key={chainId}
                      variant="tab-blue"
                      onClick={vm.set}
                      onClickEmit="name-value"
                      name="selectedChain"
                      value={chainId}
                      size="narrow-fixed"
                      title={chainDetails.chainName}
                      active={chainId === vm.selectedChain}>
                      <Icon name={icon.name} color={icon.color} size={24} />
                    </Button>
                  )
                })}
              </div>
            </div>

            {!latestUserTransactions.loaded && !latestUserTransactions.error && loader}

            {latestUserTransactions.loaded && (
              <div>
                <p className="txt-warning txt-center">{vm.errorMessage}</p>

                <div className="event-info__grid profile txt-left">
                  {vm.currentTransactions.map(
                    (info, i) => info && <EventInfo info={info} currentUser key={info.txHash + i} />
                  )}
                </div>
              </div>
            )}
            {vm.quantityTxPagination > 1 && (
              <div className="pagination padded-bottom-0">
                <div
                  className={`prev ${vm.txPagination === 0 ? `disabled` : ``}`}
                  onClick={vm.prevTxPagination}>
                  <ArrowPagination />
                </div>
                {vm.txPaginations.map((pagination) => (
                  <Button
                    size={'narrow'}
                    key={'pagination' + pagination}
                    variant="invisible"
                    name="txPagination"
                    value={pagination}
                    onClick={vm.set}
                    onClickEmit="name-value">
                    <span
                      className={`padded-h-1 ${
                        pagination !== vm.txPagination ? 'txt-muted' : 'txt-title'
                      }`}>
                      {++pagination}
                    </span>
                  </Button>
                ))}
                <div
                  className={`next ${vm.isLastTxPagination ? `disabled` : ``}`}
                  onClick={vm.nextTxPagination}>
                  <ArrowPagination />
                </div>
              </div>
            )}
          </div>
        </div>

        <Modal
          isOpen={vm.avatarDialog.visible}
          onRequestClose={vm.closeDialog}
          onAfterClose={vm.closeDialog}>
          <AvatarSelector vm={vm} />
        </Modal>
      </main>
    </div>
  )
}

export default observer(UserProfile)
